import React, { useCallback, useEffect, useRef, useState } from "react";
import EditEvent from "../../../components/ui/EditEvent";
import axiosInstance from "../../../api/axiosInstance";
import { backendUrl } from "../../../api/constants";
import { useNavigate } from "react-router-dom";
import { usePortalContext } from "../../../context/PortalContext/portalcontext";
import { PortalTitle } from "../../Portal";
import KitButton from "../../../components/kit/Button";
import { errorSummary } from "../../../util/formErrors";
import { HiOutlineDocumentDuplicate } from "react-icons/hi2";
import Input from "../../../components/Input/Input";
import { useUser } from "../../../context/UserContext/UserProvider";
import { useLogger } from "../../../context/LogContext/LogProvider";
import { modal } from "../../../util/useModal";
import { enqueueSnackbar } from "notistack";
import { ErrorMessage } from "../../../components/Input/InputError";
import { BsPlusLg } from "react-icons/bs";
import VerifyEmail from "../../Auth/VerifyEmail/VerifyEmail";
import EventPostingWizard from "../../../components/EventPostingWizard/EventPostingWizard";
import { useEventWizard } from "../../../hooks/useEventWizard";
import TemplatePicker from "../../../components/TemplatePicker/TemplatePicker";
import { formatAddressComponents } from "lib";
import { DateTime } from "luxon";
import LineupTab from "../../../components/LineupTab/LineupTab";
import { ROSTER_FEATURE_ENABLED } from "../../../config/features";
import PerformerSlotsEditor from "../../../components/PerformerSlotsEditor/PerformerSlotsEditor";
import AppStatusScreen from "../../../components/AppStatusScreen/AppStatusScreen";
import EventDetails from "../../../components/EventDetails/EventDetails";
import { normalizeEventTicketsInForm, normalizeTicketsForBackend } from "../../../util/ticketUtils";

const getWizardLocationFields = (wizardLocation = {}) => {
  const address = wizardLocation.addressFields
    ? formatAddressComponents(wizardLocation.addressFields)
    : (wizardLocation.address || "");
  const geoLocation = wizardLocation.coordinates
    ? {
        type: "Point",
        coordinates: [wizardLocation.coordinates.lng, wizardLocation.coordinates.lat],
      }
    : null;

  return {
    address,
    addressFields: wizardLocation.addressFields || null,
    location: geoLocation,
    venue: wizardLocation.venue || null,
    venueId: wizardLocation.venue?._id || null,
  };
};

const getLinkedArtistIds = (artists = []) => {
  return artists.map((artist) => artist?._id || artist).filter(Boolean);
};

const EventSubmittedActions = ({
  onSaveTemplate,
  onPostSimilar,
  onEditEvent,
  onDone,
  showEdit = true,
  disabled = false,
}) => (
  <div className="flex flex-col items-center gap-kit-20">
    <div className="flex w-full flex-wrap justify-center gap-kit-8">
      <KitButton variant="secondary" size="sm" onClick={onSaveTemplate} disabled={disabled}>
        save as template
      </KitButton>
      <KitButton variant="secondary" size="sm" onClick={onPostSimilar} disabled={disabled}>
        post similar
      </KitButton>
      {showEdit && (
        <KitButton variant="secondary" size="sm" onClick={onEditEvent} disabled={disabled}>
          edit event
        </KitButton>
      )}
    </div>
    <KitButton variant="primary" fullWidth className="max-w-[9.4rem]" onClick={onDone} disabled={disabled}>
      done
    </KitButton>
  </div>
);

const getTimezone = (eventData) => {
  if (Array.isArray(eventData?.timezone) && eventData.timezone[0]) return eventData.timezone[0];
  if (typeof eventData?.timezone === "string" && eventData.timezone) return eventData.timezone;
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const parseDateTime = (value, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  if (!value) return null;
  if (typeof value === "string") {
    const parsed = DateTime.fromISO(value, { setZone: true });
    return parsed.isValid ? parsed.setZone(timezone) : null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return DateTime.fromJSDate(date, { zone: timezone });
};

const toInputDateTime = (dateTime) => dateTime.toFormat("yyyy-MM-dd'T'HH:mm");

const composeTemplateTimeOnDraftDate = (draftDate, templateDate, templateData) => {
  if (!draftDate?.start || !templateDate?.start || !templateDate?.end) return draftDate;

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const templateTimezone = getTimezone(templateData);
  const draftStart = parseDateTime(draftDate.start, localTimezone);
  const templateStart = parseDateTime(templateDate.start, templateTimezone);
  const templateEnd = parseDateTime(templateDate.end, templateTimezone);
  if (!draftStart?.isValid || !templateStart?.isValid || !templateEnd?.isValid) return draftDate;

  const nextStart = draftStart.set({
    hour: templateStart.hour,
    minute: templateStart.minute,
    second: 0,
    millisecond: 0,
  });
  let nextEnd = draftStart.set({
    hour: templateEnd.hour,
    minute: templateEnd.minute,
    second: 0,
    millisecond: 0,
  });
  if (nextEnd <= nextStart) nextEnd = nextEnd.plus({ days: 1 });

  return {
    start: toInputDateTime(nextStart),
    end: toInputDateTime(nextEnd),
  };
};

export const EventPostingSurface = ({
  userLocation,
  variant = "page",
  initialEventData = null,
  onClose,
  onDirtyChange,
  onDateChange,
  onSubmitted,
  lockedRosterId,
  lockedRosterName,
  showTemplateAction = false,
  hideArtistTagging = false,
}) => {
  const { user } = useUser();
  const {
    editEvent,
    setEditEvent,
    setPrefillEventData,
    prefillEventData,
    setCurrentTab,
    templatePickerRequestId,
    guidedPostingRequestId,
    navigateToEditEvent,
    targetEventPortalTab,
    setTargetEventPortalTab,
  } = usePortalContext();
  const [loading, setLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [submitConfirming, setSubmitConfirming] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [postError, setPostError] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [portalTab, setPortalTab] = useState("event");
  const eventRef = useRef();
  const slotsRef = useRef();
  const previewObjectUrlsRef = useRef([]);
  const isBusiness = user?.accountType === "business";
  const isModalVariant = variant === "modal";
  const navigate = useNavigate();

  const { startLog } = useLogger();
  
  // Event Posting Wizard hook
  const wizard = useEventWizard();
  const [wizardPrefillData, setWizardPrefillData] = useState(null);
  const [isWizardPosting, setIsWizardPosting] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const cleanupPreviewObjectUrls = () => {
    previewObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewObjectUrlsRef.current = [];
  };

  const closePreview = () => {
    cleanupPreviewObjectUrls();
    setPreviewData(null);
  };
  const eventData = editEvent || prefillEventData || wizardPrefillData || initialEventData;
  const [liveEventDate, setLiveEventDate] = useState(eventData?.date || null);
  const isEditing = !!eventData?._id;
  const showPerformerSlots = ROSTER_FEATURE_ENABLED && isBusiness;
  const canManageLineup = ROSTER_FEATURE_ENABLED && isBusiness && isEditing;
  const portalTabs = canManageLineup ? ["event", "lineup"] : ["event"];
  const submitLabel = isEditing ? "save edits" : "submit event";

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (isModalVariant) setIsDirty(false);
  }, [isModalVariant, eventData?._id, initialEventData]);

  useEffect(() => {
    setLiveEventDate(eventData?.date || null);
    onDateChange?.(eventData?.date || null);
  }, [eventData?.date, eventData?._id, onDateChange]);

  useEffect(() => {
    return () => {
      cleanupPreviewObjectUrls();
      if (editEvent) {
        setEditEvent(null);
      }
    };
  }, []);

  useEffect(() => {
    if (!targetEventPortalTab || !canManageLineup) return;
    setPortalTab(targetEventPortalTab);
    setTargetEventPortalTab(null);
  }, [targetEventPortalTab, canManageLineup, setTargetEventPortalTab]);

  const lastTemplatePickerRequestRef = useRef(templatePickerRequestId);
  const lastGuidedPostingRequestRef = useRef(guidedPostingRequestId);

  useEffect(() => {
    if (!templatePickerRequestId || lastTemplatePickerRequestRef.current === templatePickerRequestId) return;
    lastTemplatePickerRequestRef.current = templatePickerRequestId;
    setShowTemplatePicker(true);
  }, [templatePickerRequestId]);

  useEffect(() => {
    if (!guidedPostingRequestId || lastGuidedPostingRequestRef.current === guidedPostingRequestId) return;
    lastGuidedPostingRequestRef.current = guidedPostingRequestId;
    wizard.openWizard();
  }, [guidedPostingRequestId, wizard]);

  // Clear prefill data after it's been used
  useEffect(() => {
    if (prefillEventData) {
      // Clear after a delay - must be longer than modal animation (300ms) 
      // to ensure EditEvent has received the data after modal closes
      const timeout = setTimeout(() => {
        setPrefillEventData(null);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [prefillEventData]);

  const handleError = (err) => {
    return err;
  };

  const handleNewAction = () => {
    setEditEvent(null);
    setPrefillEventData(null);
    setWizardPrefillData(null);
    navigate("/dashboard?tab=new-event");
  };

  const handlePostedAction = () => {
    setEditEvent(null);
    setCurrentTab("posted-events");
  };

  const handleTemplateSelect = (templateData) => {
    const currentDate = liveEventDate || eventData?.date;
    const composedDate = composeTemplateTimeOnDraftDate(
      currentDate,
      templateData?.date,
      templateData
    );
    const nextEventData = {
      ...templateData,
      ...(composedDate ? { date: composedDate } : {}),
    };
    setEditEvent(nextEventData);
    setShowTemplatePicker(false);
    markDirty();
  };

  const finishPostingFlow = (proceed) => {
    proceed();
    if (isModalVariant) {
      onClose?.();
      return;
    }
    navigate("/");
  };

  const goToRosterMembers = (rosterId) => {
    if (!rosterId) return;
    const params = new URLSearchParams({
      tab: "bookings",
      bookingsTab: "rosters",
      rosterId,
      rosterTab: "members",
    });
    navigate(`/dashboard?${params.toString()}`);
  };

  const markDirty = () => {
    if (isModalVariant) setIsDirty(true);
  };

  const handleDateChange = useCallback((date) => {
    setLiveEventDate(date);
    onDateChange?.(date);
  }, [onDateChange]);

  const addPerformerSlotsToForm = (form) => {
    if (!showPerformerSlots || !slotsRef.current || !form?.has("event")) return form;

    const event = JSON.parse(form.get("event") || "{}");
    const slotsData = slotsRef.current.getSlotsData(event.date);
    if (slotsData.error) return { error: slotsData.error };
    event.roster = slotsData.roster;
    event.performerSlots = slotsData.performerSlots.filter(
      (slot) => slot.title?.trim() || slot.start || slot.end
    );
    form.set("event", JSON.stringify(event));
    return form;
  };

  const getEventFormData = async (eventOverrides) => {
    const result = await eventRef.current?.getFormData(eventOverrides);
    if (result?.form && !result?.error) {
      const slotResult = addPerformerSlotsToForm(result.form);
      if (slotResult?.error) return { ...result, error: slotResult.error };
    }
    return result;
  };

  const onSubmit = async () => {
    console.log("submitting event");
    const log = startLog({
      user: user,
      event: "submitting event",
    });
    log.log("Start submitting event");

    setLoading(true);
    try {
      log.log("Retrieving form data");
      const { form, error, errors } = await getEventFormData();
      console.log("retrieve form data", form, error);
      if (error) {
        log.log("[Error] Form Data validation failed: ", error);
        eventRef.current?.focusFirstError?.(errors);
        enqueueSnackbar(error, { variant: "error" });
        setPostError(error);
        return;
      }

      normalizeEventTicketsInForm(form);

      const url = `${backendUrl}/influencer/${
        isEditing ? "edit" : "post"
      }`;
      log.log("Posting form data: ", form, " to url :", url);
      const { data } = await axiosInstance.post(url, form, {
        "Content-Type": "multipart/form-data",
      });

      log.log("Received response: ", data);

      if (data.error) {
        log.log("[Error] Error in data: ", data);
        // if its a string then its a general error
        if (typeof data.error === "string") {
          enqueueSnackbar(data.error, { variant: "error" });
          setPostError(data.error);
          return;
        } else {
          // field errors (the server returns all of them): mark every field + one summary
          if (data.error.address) {
            eventRef.current?.setAddressErrors?.(data.error.address.error || data.error.address);
          }
          eventRef.current?.focusFirstError?.(data.error);
          const summary = errorSummary(data.error) || "please check the highlighted fields";
          enqueueSnackbar(summary, { variant: "error" });
          setPostError(summary);
          return;
        }
      } else {
        log.log("[Success] Event submitted successfully");

        const recommend = eventRef.current?.recommend()?.checked;
        console.log(">>>>>>>> recommend", recommend);
        if (recommend) {
          log.log("Recommending event");
          const jsonObject = {};
          for (const [key, value] of form.entries()) {
            // Assuming 'event' field is a JSON string, parse it back to an object
            if (key === "event") {
              jsonObject[key] = JSON.parse(value);
            } else {
              jsonObject[key] = value;
            }
          }
          log.log("Posting recommendation: ", jsonObject?.event);
          const { data: rec } = await axiosInstance.post(
            `${backendUrl}/influencer/recommendations`,
            jsonObject?.event,
            {
              "Content-Type": "multipart/form-data",
            }
          );

          if (rec.error) {
            log.log("[Error] Error in recommendation: ", rec.error);
            console.log(rec.error);
          }
        }

        //post the events using _extraDates if it exists
        // _extraDates is an array of objects under the 'event' object in the form
        if (form.has("_extraDates")) {
          log.log("Posting extra dates");
          const extraDates = JSON.parse(form.get("_extraDates"));
          form.delete("_extraDates");
          for (const date of extraDates) {
            //TODO change date to date.date and rrule to date.rrule within the event field
            const event = JSON.parse(form.get("event"));
            event.date = date.date;
            event.rrule = date.rrule;
            form.set("event", JSON.stringify(event));

            // console log form as object
            const formObject = {};
            for (const [key, value] of form.entries()) {
              formObject[key] = value;
            }
            console.log("Posting extra date: ", formObject);
            const { data: extraDate } = await axiosInstance.post(
              `${backendUrl}/influencer/post`,
              form,
              {
                "Content-Type": "multipart/form-data",
              }
            );

            if (extraDate.error) {
              log.log("[Error] Error in extra date: ", extraDate.error);
              console.log(extraDate.error);
            }
          }
        }

        //show success message
        log.log("[Success] All operations completed successfully");
        
        // Store form data for potential "post similar event" action
        const currentFormData = await getEventFormData();
        const postedEventId = data?.event?._id || data?._id;
        const postedRosterInfo = slotsRef.current?.getSelectedRosterInfo?.();
        const shouldShowEmptyRosterGuidance =
          isModalVariant &&
          !isEditing &&
          postedRosterInfo?._id &&
          (postedRosterInfo.memberCount || 0) === 0;
        setIsDirty(false);
        onDirtyChange?.(false);
        onSubmitted?.(data?.event || data);
        
        // Don't navigate away - let the modal handle navigation
        // navigate("/");
        
        const notificationsEnabled = user?.settings?.notifications !== false;
        
        if (isEditing) {
          // Edit flow - simple confirmation
          modal(({ proceed }) => (
            <AppStatusScreen
              title="edits submitted!"
              body={
                notificationsEnabled ? (
                  <p>after we approve or reject your event, you'll receive an email confirmation.</p>
                ) : (
                  <p>our team will review your changes.</p>
                )
              }
              actions={
                <KitButton
                  variant="primary"
                  onClick={() => {
                    proceed();
                    if (isModalVariant) {
                      onClose?.();
                    } else {
                      navigate("/");
                    }
                  }}
                >
                  done
                </KitButton>
              }
            />
          ), { fullScreen: true, raw: true });
          setEditEvent(null);
        } else {
          // New event flow - enhanced success modal with actions
          if (editEvent) {
            setEditEvent(null);
          }
          
          modal(({ proceed }) => {
            const [templateName, setTemplateName] = useState("");
            const [showTemplateInput, setShowTemplateInput] = useState(false);
            const [templateError, setTemplateError] = useState(null);
            const [savingTemplate, setSavingTemplate] = useState(false);
            
            const handleSaveTemplate = async () => {
              if (savingTemplate) return;
              if (!templateName.trim()) {
                setTemplateError("please enter a template name");
                return;
              }
              if (templateName.trim().length < 3) {
                setTemplateError("template name must be at least 3 characters");
                return;
              }
              
              setSavingTemplate(true);
              try {
                const { form } = currentFormData || {};
                if (form) {
                  form.set("templateName", templateName.trim());
                  form.delete("_id");
                  
                  const { data: tmpdata } = await axiosInstance.post(
                    `${backendUrl}/influencer/templates/save`,
                    form
                  );
                  
                  if (tmpdata?.error) {
                    setTemplateError(typeof tmpdata.error === "string" ? tmpdata.error : "failed to save template");
                  } else {
                    enqueueSnackbar(`template "${templateName}" saved!`, { variant: "success" });
                    setShowTemplateInput(false);
                    setTemplateName("");
                  }
                }
              } catch (error) {
                setTemplateError("failed to save template");
              } finally {
                setSavingTemplate(false);
              }
            };
            
            const handlePostSimilar = () => {
              if (savingTemplate) return;
              console.log("[DEBUG] handlePostSimilar called");
              
              // Store form data (minus dates and _id) for prefill
              if (currentFormData?.form) {
                const eventDataRaw = currentFormData.form.get("event");
                const eventData = JSON.parse(eventDataRaw || "{}");
                // Remove date-specific fields so user enters new dates
                delete eventData.date;
                delete eventData.rrule;
                // Remove _id so it's treated as a new event, not an edit
                delete eventData._id;
                console.log("[DEBUG] Setting prefillEventData:", eventData);
                setPrefillEventData(eventData);
              }
              
              // Clear any existing edit event
              setEditEvent(null);
              
              // Close modal - we stay on the same tab since we removed navigate("/")
              proceed();
              console.log("[DEBUG] handlePostSimilar complete - staying on portal");
            };
            
            const handleEditEvent = () => {
              if (savingTemplate) return;
              console.log("[DEBUG] handleEditEvent called, postedEventId:", postedEventId);
              if (postedEventId) {
                proceed();
                navigateToEditEvent(postedEventId);
              }
            };

            const handleInviteArtists = () => {
              if (savingTemplate) return;
              proceed();
              onClose?.();
              goToRosterMembers(postedRosterInfo?._id);
            };

            const handlePostMoreEvents = () => {
              if (savingTemplate) return;
              proceed();
              onClose?.();
            };
            
            return (
              <AppStatusScreen
                title={
                  showTemplateInput
                    ? "save as template"
                    : shouldShowEmptyRosterGuidance
                    ? "event submitted - roster is empty"
                    : "event submitted!"
                }
                subtitle={
                  showTemplateInput
                    ? "name it something memorable!"
                    : shouldShowEmptyRosterGuidance
                    ? `${postedRosterInfo?.displayName || "this roster"} has no artists yet`
                    : undefined
                }
                body={
                  showTemplateInput ? undefined : (
                    <>
                      {shouldShowEmptyRosterGuidance ? (
                        <>
                          <p>artists won&apos;t see or apply to this lineup until the roster has members.</p>
                          <p>invite artists now, or keep posting events and come back later from bookings &gt; rosters.</p>
                        </>
                      ) : (
                        <>
                          <p>now, a human is going to vet your event submission</p>
                          <p>we&apos;ll email when it&apos;s approved or declined</p>
                          <p>edit and check the status of your event at any time by tapping &quot;+&quot; and selecting &quot;posted&quot;</p>
                        </>
                      )}
                    </>
                  )
                }
                centerClassName={showTemplateInput ? "mt-kit-24" : "mt-kit-16"}
                actions={
                  showTemplateInput ? (
                    <div className="flex w-full flex-col gap-kit-16">
                      <div className="flex gap-kit-12">
                        <KitButton
                          variant="secondary"
                          className="flex-1"
                          onClick={() => {
                            if (savingTemplate) return;
                            setShowTemplateInput(false);
                            setTemplateName("");
                            setTemplateError(null);
                          }}
                          disabled={savingTemplate}
                        >
                          cancel
                        </KitButton>
                        <KitButton variant="primary" className="flex-1" onClick={handleSaveTemplate} loading={savingTemplate}>
                          save
                        </KitButton>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-kit-20">
                      {shouldShowEmptyRosterGuidance ? (
                        <div className="flex w-full flex-col items-center gap-kit-10">
                          <KitButton variant="primary" fullWidth className="max-w-[13rem]" onClick={handleInviteArtists} disabled={savingTemplate}>
                            invite artists now
                          </KitButton>
                          <KitButton variant="secondary" fullWidth className="max-w-[13rem]" onClick={handlePostMoreEvents} disabled={savingTemplate}>
                            post more events
                          </KitButton>
                          <KitButton
                            variant="tertiary"
                            fullWidth
                            className="max-w-[13rem]"
                            onClick={() => {
                              if (savingTemplate) return;
                              finishPostingFlow(proceed);
                            }}
                            disabled={savingTemplate}
                          >
                            do it later
                          </KitButton>
                        </div>
                      ) : (
                        <EventSubmittedActions
                          onSaveTemplate={() => setShowTemplateInput(true)}
                          onPostSimilar={handlePostSimilar}
                          onEditEvent={handleEditEvent}
                          onDone={() => {
                            if (savingTemplate) return;
                            finishPostingFlow(proceed);
                          }}
                          showEdit={Boolean(postedEventId)}
                          disabled={savingTemplate}
                        />
                      )}
                    </div>
                  )
                }
              >
                {showTemplateInput && (
                  <div className="inyc-fields mt-kit-40 w-full max-w-[24rem]">
                    <Input
                      placeHolder="template name"
                      value={templateName}
                      onChange={(e) => {
                        setTemplateName(e.target.value);
                        setTemplateError(null);
                      }}
                      className="!mt-0"
                      inputStyle={{ textAlign: "center" }}
                      error={templateError || undefined}
                    />
                  </div>
                )}
              </AppStatusScreen>
            );
          }, { fullScreen: true, raw: true });
        }
      }
    } catch (error) {
      console.error("[submit event] failed", error, error?.response?.data);
      log.log("[Error] Exception caught on the main function call: ", error);
      const serverError =
        (typeof error?.response?.data?.error === "string" && error.response.data.error) ||
        error?.response?.data?.message ||
        error?.message;
      enqueueSnackbar(serverError || "something went wrong, please try again", { variant: "error" });
      setPostError(serverError || "something went wrong, please try again");
      return { error: serverError || "something went wrong, please try again" };
    } finally {
      log.log("Setting loading to false and sending logs");
      setLoading(false);
      log.send();
    }
  };

  const saveAsTemplate = async () => {
    if (savingTemplate || loading) return;
    try {
      const log = startLog({
        user: user,
        event: "saving template",
      });
      const { form, error, errors } = await getEventFormData();
      if (error) {
        log.log("[Error] Form Data validation failed: ", error);
        eventRef.current?.focusFirstError?.(errors);
        setPostError(error);
        return;
      }
      log.log("Saving template");
      const res = await modal(
        ({ proceed }) => {
          const [templateError, setTemplateError] = useState(null);
          const templateNameRef = useRef();
          const handleTemplateSubmit = async () => {
            if (templateNameRef.current?.value.trim() === "")
              return setTemplateError("please enter a template name");
            //if its less than 3 characters
            if (templateNameRef.current?.value.trim().length < 3)
              return setTemplateError(
                "template name must be at least 3 characters"
              );

            proceed(templateNameRef.current?.value.trim());
          };
          return (
            <div>
              <div className="mb-kit-8 font-['Primary-Bold'] text-kit-lg font-bold">
                title your template
              </div>
              <Input
                placeHolder="template name"
                className="mx-auto mb-kit-16 w-full max-w-[19rem] !border-[#8a8a8a] !py-kit-2 [&>div]:flex [&>div]:items-center"
                inputStyle={{
                  padding: "var(--inyc-space-2) var(--inyc-space-10)",
                  lineHeight: "1.2rem",
                  transform: "translateY(-1px)",
                }}
                Ref={templateNameRef}
              />
              <div className="mb-kit-16 !text-kit-sm">
                access your templates by tapping
                <div className="h-[1.5rem] translate-y-[25%] leading-[2rem] w-[1.5rem] inline-flex p-kit-4 justify-center items-center rounded-[50%] mx-kit-8 bg-[var(--inyc-primary-light)] text-kit-lg text-[var(--inyc-primary-text-color)]">
                  <BsPlusLg size={15} />
                </div>
                <br />
                and then "templates"
              </div>
              <div className="flex justify-center items-center gap-kit-16 mb-kit-16">
                <KitButton variant="secondary" onClick={() => proceed(null)}>
                  cancel
                </KitButton>
                <KitButton variant="primary" onClick={handleTemplateSubmit}>
                  submit
                </KitButton>
              </div>
              {templateError && (
                <div className="text-kit-md text-danger">
                  {templateError}
                </div>
              )}
            </div>
          );
        },
        {
          hideCloseButton: true,
        }
      );

      log.log('Retrieved template name: "', res, '"');
      if (res) {
        form.set("templateName", res);
        //remove _id from form
        form.delete("_id");

        try {
          setSavingTemplate(true);
          log.log("Saving template to backend: ", form);
          const { data: tmpdata } = await axiosInstance.post(
            `${backendUrl}/influencer/templates/save`,
            form
          );

          log.log("Received response from backend: ", tmpdata);
          if (tmpdata?.error) {
            log.log("[Error] Error in template: ", tmpdata.error);
            if (typeof tmpdata.error === "string") {
              setPostError(tmpdata.error);
              return;
            } else if (typeof tmpdata.error === "object") {
              for (const key in tmpdata.error) {
                if (tmpdata.error[key].hasOwnProperty("error")) {
                  setPostError(tmpdata.error[key].error);
                  return;
                }
              }

              return;
            }
          } else {
            log.log("[Success] Template saved successfully");
            setPostError(null);
            enqueueSnackbar(`template "${res}" saved successfully`, {
              variant: "success",
            });
          }
        } catch (error) {
          log.log("[Error] Exception caught while saving template: ", error);
          await log.display();
          setPostError("something went wrong, please try again later");
          return;
        } finally {
          setSavingTemplate(false);
        }
      }
    } catch (error) {
      console.log(error);
      return setPostError("something went wrong, please try again later");
    }
  };

  const previewEvent = async () => {
    if (savingTemplate || loading) return;

    const createdObjectUrls = [];

    try {
      const { form, error, previewDetails } = await getEventFormData({ _includePreviewDetails: true });

      if (error) {
        enqueueSnackbar(error, { variant: "error" });
        setPostError(error);
        return;
      }

      if (!form?.has("event")) {
        setPostError("could not preview event");
        return;
      }

      const draftEvent = JSON.parse(form.get("event") || "{}");
      const uploadedImages = form
        .getAll("images")
        .filter((item) => typeof File !== "undefined" && item instanceof File)
        .map((file) => {
          const objectUrl = URL.createObjectURL(file);
          createdObjectUrls.push(objectUrl);
          return { original: objectUrl };
        });

      const previewData = {
        ...draftEvent,
        _id: draftEvent._id || `event-preview-${Date.now()}`,
        type: "event",
        account_id: draftEvent.account_id || user,
        likes: draftEvent.likes || [],
        // the form holds local "yyyy-MM-ddTHH:mm" strings; EventDetails expects Dates (as API events have)
        ...(draftEvent.date
          ? {
              date: {
                start: draftEvent.date.start ? new Date(draftEvent.date.start) : draftEvent.date.start,
                end: draftEvent.date.end ? new Date(draftEvent.date.end) : draftEvent.date.end,
              },
            }
          : {}),
        ...(previewDetails?.venue
          ? { venue: previewDetails.venue, venueDetails: previewDetails.venue }
          : {}),
        ...(previewDetails?.linkedArtists
          ? { linked_artists: previewDetails.linkedArtists }
          : {}),
        images: [...uploadedImages, ...(draftEvent.images || [])],
      };

      cleanupPreviewObjectUrls();
      previewObjectUrlsRef.current = createdObjectUrls;
      setPreviewData(previewData);
    } catch (error) {
      console.log(error);
      createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
      setPostError("could not preview event");
    }
  };

  // Handle wizard completion - convert wizard data to form prefill format
  const handleWizardComplete = (data) => {
    const wizardLocation = getWizardLocationFields(data.location);
    const prefillData = {
      name: data.name,
      category: data.category,
      tags: data.tags || [],
      description: data.description,
      address: wizardLocation.address,
      addressFields: wizardLocation.addressFields,
      location: wizardLocation.location,
      venue: wizardLocation.venue,
      ticketed: data.ticketed,
      tickets: data.tickets,
      socials: data.socials, // Pass socials from wizard
      linked_artists: data.linked_artists || [],
      age_restriction: data.age_restriction || '',
      // Convert date format
      date: data.date.startDate && data.date.startTime && data.date.endTime ? {
        start: `${data.date.startDate}T${data.date.startTime}`,
        end: `${data.date.startDate}T${data.date.endTime}`,
      } : null,
      // Images will be handled separately by EditEvent if needed
      _wizardImages: data.images,
    };
    
    // Handle recurrence data from wizard
    const { recurrence } = data;
    if (recurrence) {
      if (recurrence.type === 'repeating') {
        // Convert wizard recurrence to rrule format
        const freqMap = { day: 3, week: 2, month: 1 }; // RRule frequency constants
        const dayToRRuleMap = { sun: 6, mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5 };
        
        const rruleOptions = {
          freq: freqMap[recurrence.repeatEveryPeriod] || 2,
          interval: recurrence.repeatEveryNumber || 1,
          dtstart: data.date.startDate ? `${data.date.startDate}T${data.date.startTime}` : null,
          until: recurrence.repeatEndsOn ? `${recurrence.repeatEndsOn}T23:59:59` : null,
          tzid: 'utc',
        };
        
        // Handle weekly: add byweekday
        if (recurrence.repeatEveryPeriod === 'week' && recurrence.repeatOn?.length > 0) {
          rruleOptions.byweekday = recurrence.repeatOn.map(day => dayToRRuleMap[day]);
        }
        
        // Handle monthly: nth weekday (e.g., "2nd Monday")
        if (recurrence.repeatEveryPeriod === 'month' && recurrence.monthlyType === 'dayOfWeek' && data.date.startDate) {
          const [yr, mo, dy] = data.date.startDate.split('-').map(Number);
          const weekOfMonth = Math.ceil(dy / 7);
          const dayOfWeek = new Date(yr, mo - 1, dy).getDay();
          const rruleDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          rruleOptions.byweekday = [{ weekday: rruleDayOfWeek, n: weekOfMonth }];
        }
        
        prefillData.rrule = { origOptions: rruleOptions };
        prefillData._wizardRecurring = true;
        prefillData._wizardRepeatOn = recurrence.repeatOn || [];
        prefillData._wizardRepeatEveryPeriod = recurrence.repeatEveryPeriod;
      } else if (recurrence.type === 'multiple' && recurrence.extraDates?.length > 0) {
        // Convert wizard extra dates to EditEvent format
        prefillData._wizardExtraDates = recurrence.extraDates.map(d => ({
          startDate: d.startDate ? new Date(d.startDate) : null,
          startTime: d.startTime,
          endTime: d.endTime,
        })).filter(d => d.startDate); // Only include valid dates
      }
    }
    
    setWizardPrefillData(prefillData);
    wizard.closeWizard();
  };

  // Handle direct posting from wizard (without going to posting page)
  const handleWizardPostNow = async (data) => {
    const log = startLog({
      user: user,
      event: "posting event from wizard",
    });
    log.log("Start posting event directly from wizard");

    setIsWizardPosting(true);
    
    try {
      // Build FormData directly from wizard data
      const form = new FormData();
      
      // Build date object
      let dateObj = null;
      if (data.date.startDate && data.date.startTime && data.date.endTime) {
        const startDateTime = `${data.date.startDate}T${data.date.startTime}`;
        let endDateTime = `${data.date.startDate}T${data.date.endTime}`;
        
        // Handle end time past midnight
        if (data.date.endTime < data.date.startTime) {
          const [yr, mo, dy] = data.date.startDate.split('-').map(Number);
          const next = new Date(yr, mo - 1, dy + 1);
          endDateTime = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}T${data.date.endTime}`;
        }
        
        dateObj = {
          start: startDateTime,
          end: endDateTime,
        };
      }
      
      // Build rrule if repeating
      let rrule = null;
      if (data.recurrence?.type === 'repeating') {
        const freqMap = { day: 3, week: 2, month: 1 };
        const dayToRRuleMap = { sun: 6, mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5 };
        
        const rruleOptions = {
          freq: freqMap[data.recurrence.repeatEveryPeriod] || 2,
          interval: data.recurrence.repeatEveryNumber || 1,
          dtstart: dateObj?.start || null,
          until: data.recurrence.repeatEndsOn ? `${data.recurrence.repeatEndsOn}T23:59:59` : null,
          tzid: 'utc',
        };
        
        if (data.recurrence.repeatEveryPeriod === 'week' && data.recurrence.repeatOn?.length > 0) {
          rruleOptions.byweekday = data.recurrence.repeatOn.map(day => dayToRRuleMap[day]);
        }
        
        if (data.recurrence.repeatEveryPeriod === 'month' && data.recurrence.monthlyType === 'dayOfWeek' && data.date.startDate) {
          const [yr, mo, dy] = data.date.startDate.split('-').map(Number);
          const weekOfMonth = Math.ceil(dy / 7);
          const dayOfWeek = new Date(yr, mo - 1, dy).getDay();
          const rruleDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          rruleOptions.byweekday = [{ weekday: rruleDayOfWeek, n: weekOfMonth }];
        }
        
        rrule = { origOptions: rruleOptions };
      }
      
      const wizardLocation = getWizardLocationFields(data.location);
      
      // Build event object
      const event = {
        name: data.name,
        category: data.category,
        tags: data.tags || [],
        description: data.description || '',
        date: dateObj,
        rrule: rrule,
        address: wizardLocation.address,
        addressFields: wizardLocation.addressFields,
        location: wizardLocation.location,
        venue: wizardLocation.venueId,
        socials: data.socials || [],
        linked_artists: getLinkedArtistIds(data.linked_artists),
        age_restriction: data.age_restriction || undefined,
        ticketed: data.ticketed ? "on" : undefined,
        tickets: data.ticketed ? normalizeTicketsForBackend(data.tickets) : [],
        images: [], // Backend expects this array to exist (spreads it)
      };
      
      // Remove empty fields
      for (let key in event) {
        if (event[key] === undefined || event[key] === '' || event[key] === null) {
          delete event[key];
        }
      }
      
      // Add images to form
      if (data.images && data.images.length > 0) {
        for (let img of data.images) {
          if (img.file) {
            form.append("images", img.file);
          }
        }
      }
      
      // Build extra dates array for 'multiple' recurrence type
      const wizardExtraDates = data.recurrence?.type === 'multiple' && data.recurrence.extraDates?.length > 0
        ? data.recurrence.extraDates.map(d => {
            if (!d.startDate) return null;
            const startDateTime = `${d.startDate}T${d.startTime || data.date.startTime}`;
            let endDateTime = `${d.startDate}T${d.endTime || data.date.endTime}`;
            const startTime = d.startTime || data.date.startTime;
            const endTime = d.endTime || data.date.endTime;
            if (endTime < startTime) {
              const [yr, mo, dy] = d.startDate.split('-').map(Number);
              const next = new Date(yr, mo - 1, dy + 1);
              endDateTime = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}T${endTime}`;
            }
            return { date: { start: startDateTime, end: endDateTime } };
          }).filter(Boolean)
        : [];

      form.append("event", JSON.stringify(event));
      
      log.log("Posting form data from wizard");
      const { data: responseData } = await axiosInstance.post(
        `${backendUrl}/influencer/post`,
        form,
        { "Content-Type": "multipart/form-data" }
      );
      
      log.log("Received response: ", responseData);

      // Post extra dates as separate events (same pattern as the normal posting flow)
      if (!responseData.error && wizardExtraDates.length > 0) {
        log.log("Posting extra dates from wizard", wizardExtraDates.length);
        for (const extraDate of wizardExtraDates) {
          const extraEvent = { ...event, date: extraDate.date };
          delete extraEvent.rrule;
          const extraForm = new FormData();
          // Re-attach images
          if (data.images && data.images.length > 0) {
            for (const img of data.images) {
              if (img.file) extraForm.append("images", img.file);
            }
          }
          extraForm.append("event", JSON.stringify(extraEvent));
          const { data: extraData } = await axiosInstance.post(
            `${backendUrl}/influencer/post`,
            extraForm,
            { "Content-Type": "multipart/form-data" }
          );
          if (extraData.error) {
            log.log("[Error] Error posting extra date from wizard: ", extraData.error);
          }
        }
      }

      if (responseData.error) {
        log.log("[Error] Error in response: ", responseData.error);
        if (typeof responseData.error === "string") {
          enqueueSnackbar(responseData.error, { variant: "error" });
        } else {
          for (const key in responseData.error) {
            if (responseData.error[key].hasOwnProperty("error")) {
              enqueueSnackbar(responseData.error[key].error, { variant: "error" });
              break;
            }
          }
        }
      } else {
        log.log("[Success] Event posted successfully from wizard");
        const postedEventId = responseData?.event?._id;
        const wizardDataSnapshot = { ...data }; // Capture wizard data for "post similar"
        
        wizard.closeWizard();
        wizard.resetWizardData();
        
        const notificationsEnabled = user?.settings?.notifications !== false;
        onSubmitted?.(responseData?.event || responseData);
        
        // Show success modal - same as regular posting page
        modal(({ proceed }) => {
          const [showTemplateInput, setShowTemplateInput] = useState(false);
          const [templateName, setTemplateName] = useState("");
          const [templateError, setTemplateError] = useState(null);
          const [savingTemplate, setSavingTemplate] = useState(false);
          
          const handleSaveTemplate = async () => {
            if (savingTemplate) return;
            if (!templateName.trim()) {
              setTemplateError("please enter a template name");
              return;
            }
            if (templateName.trim().length < 3) {
              setTemplateError("template name must be at least 3 characters");
              return;
            }
            
            setSavingTemplate(true);
            try {
              // Build form data from wizard data for template
              const templateForm = new FormData();
              const wizardLocation = getWizardLocationFields(wizardDataSnapshot.location);
              const templateEvent = {
                name: wizardDataSnapshot.name,
                category: wizardDataSnapshot.category,
                tags: wizardDataSnapshot.tags || [],
                description: wizardDataSnapshot.description || '',
                address: wizardLocation.address,
                addressFields: wizardLocation.addressFields,
                location: wizardLocation.location,
                venue: wizardLocation.venueId,
                socials: wizardDataSnapshot.socials || [],
                linked_artists: wizardDataSnapshot.linked_artists || [],
                age_restriction: wizardDataSnapshot.age_restriction || undefined,
                ticketed: wizardDataSnapshot.ticketed ? "on" : undefined,
                tickets: wizardDataSnapshot.ticketed ? wizardDataSnapshot.tickets : [],
                images: [],
              };
              templateForm.append("event", JSON.stringify(templateEvent));
              templateForm.append("templateName", templateName.trim());
              if (wizardDataSnapshot.images?.length > 0) {
                for (const img of wizardDataSnapshot.images) {
                  if (img.file) {
                    templateForm.append("images", img.file);
                  }
                }
              }
              
              const { data: tmpdata } = await axiosInstance.post(
                `${backendUrl}/influencer/templates/save`,
                templateForm
              );
              
              if (tmpdata?.error) {
                setTemplateError(typeof tmpdata.error === "string" ? tmpdata.error : "failed to save template");
              } else {
                enqueueSnackbar(`template "${templateName}" saved!`, { variant: "success" });
                setShowTemplateInput(false);
                setTemplateName("");
              }
            } catch (error) {
              setTemplateError("failed to save template");
            } finally {
              setSavingTemplate(false);
            }
          };
          
          const handlePostSimilar = () => {
            if (savingTemplate) return;
            // Store wizard data (minus dates) for prefill
            const wizardLocation = getWizardLocationFields(wizardDataSnapshot.location);
            const prefillData = {
              name: wizardDataSnapshot.name,
              category: wizardDataSnapshot.category,
              tags: wizardDataSnapshot.tags || [],
              description: wizardDataSnapshot.description || '',
              address: wizardLocation.address,
              addressFields: wizardLocation.addressFields,
              location: wizardLocation.location,
              venue: wizardLocation.venue,
              socials: wizardDataSnapshot.socials || [],
              linked_artists: wizardDataSnapshot.linked_artists || [],
              age_restriction: wizardDataSnapshot.age_restriction || '',
              ticketed: wizardDataSnapshot.ticketed ? "on" : undefined,
              tickets: wizardDataSnapshot.ticketed ? wizardDataSnapshot.tickets : [],
              images: [],
            };
            setPrefillEventData(prefillData);
            setEditEvent(null);
            proceed();
          };
          
          const handleEditEvent = () => {
            if (savingTemplate) return;
            if (postedEventId) {
              proceed();
              navigateToEditEvent(postedEventId);
            }
          };
          
          return (
            <AppStatusScreen
              title={showTemplateInput ? "save as template" : "event submitted!"}
              subtitle={showTemplateInput ? "name it something memorable!" : undefined}
              body={
                showTemplateInput ? undefined : (
                  <>
                    <p>now, a human is going to vet your event submission</p>
                    <p>we&apos;ll email when it&apos;s approved or declined</p>
                    <p>edit and check the status of your event at any time by tapping &quot;+&quot; and selecting &quot;posted&quot;</p>
                  </>
                )
              }
              centerClassName={showTemplateInput ? "mt-kit-24" : "mt-kit-16"}
              actions={
                showTemplateInput ? (
                  <div className="flex w-full flex-col gap-kit-16">
                    <div className="flex gap-kit-12">
                      <KitButton
                        variant="secondary"
                        className="flex-1"
                        onClick={() => {
                          if (savingTemplate) return;
                          setShowTemplateInput(false);
                          setTemplateName("");
                          setTemplateError(null);
                        }}
                        disabled={savingTemplate}
                      >
                        cancel
                      </KitButton>
                      <KitButton variant="primary" className="flex-1" onClick={handleSaveTemplate} loading={savingTemplate}>
                        save
                      </KitButton>
                    </div>
                  </div>
                ) : (
                  <EventSubmittedActions
                    onSaveTemplate={() => setShowTemplateInput(true)}
                    onPostSimilar={handlePostSimilar}
                    onEditEvent={handleEditEvent}
                    onDone={() => {
                      if (savingTemplate) return;
                      finishPostingFlow(proceed);
                    }}
                    showEdit={Boolean(postedEventId)}
                    disabled={savingTemplate}
                  />
                )
              }
            >
              {showTemplateInput && (
                <div className="inyc-fields mt-kit-40 w-full max-w-[24rem]">
                  <Input
                    placeHolder="template name"
                    value={templateName}
                    onChange={(e) => {
                      setTemplateName(e.target.value);
                      setTemplateError(null);
                    }}
                    className="!mt-0"
                    inputStyle={{ textAlign: "center" }}
                    error={templateError || undefined}
                  />
                </div>
              )}
            </AppStatusScreen>
          );
        }, { fullScreen: true, raw: true });
      }
    } catch (error) {
      log.log("[Error] Exception caught while posting from wizard: ", error);
      enqueueSnackbar("something went wrong, please try again", { variant: "error" });
    } finally {
      setIsWizardPosting(false);
      log.send();
    }
  };

  // Don't show wizard if editing an existing event
  const showWizard = wizard.isWizardOpen && !editEvent?._id && !prefillEventData && !initialEventData;

  if (user && !user.emailVerified) {
    return <VerifyEmail fromPostingPage={true} />;
  }

  return (
    <div
      className={`inyc-fields ${isModalVariant ? "min-h-full" : "pb-[5rem]"} bg-[var(--inyc-primary)]`}
      onInputCapture={markDirty}
      onChangeCapture={markDirty}
    >
      {/* Event Posting Wizard */}
      <EventPostingWizard
        isOpen={showWizard}
        onClose={wizard.closeWizard}
        onSkip={wizard.skipWizard}
        onDisableWizard={wizard.disableWizard}
        onComplete={handleWizardComplete}
        onPostNow={handleWizardPostNow}
        isPosting={isWizardPosting}
        wizardData={wizard.wizardData}
        updateWizardData={wizard.updateWizardData}
        currentStep={wizard.currentStep}
        nextStep={wizard.nextStep}
        prevStep={wizard.prevStep}
        goToStep={wizard.goToStep}
        isFirstStep={wizard.isFirstStep}
        isLastStep={wizard.isLastStep}
        isReviewStep={wizard.isReviewStep}
        progress={wizard.progress}
        totalSteps={wizard.totalSteps}
        steps={wizard.steps}
        currentStepInfo={wizard.currentStepInfo}
        direction={wizard.direction}
        isCurrentStepValid={wizard.isCurrentStepValid}
        userLocation={userLocation}
      />
      
      {/* Template Picker Modal */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handleTemplateSelect}
      />

      {previewData && (
        <div className="fixed inset-0 z-[100000] overflow-y-auto bg-[var(--inyc-primary)] text-[var(--inyc-primary-text-color)]">
          <div className="mx-auto min-h-[100vh] w-full max-w-[678px]">
            <EventDetails
              data={previewData}
              className="!pt-kit-32"
              Close={closePreview}
              previewOnly
            />
          </div>
        </div>
      )}

      {isModalVariant && (
        // plain row above the form; px-kit-32 lines the buttons up with the "title" field (EditEvent's 1rem + 16px padding)
        <div className="mt-kit-12 flex flex-wrap items-center gap-kit-8 px-kit-32">
          {showTemplateAction && !isEditing && (
            <KitButton variant="secondary" size="sm" icon={<HiOutlineDocumentDuplicate />} onClick={() => setShowTemplatePicker(true)}>
              start from template
            </KitButton>
          )}
          <KitButton variant="primary" size="sm" className="min-w-[8.5rem]" loading={loading} disabled={savingTemplate} onClick={onSubmit}>
            {submitLabel}
          </KitButton>
          {isDirty && (
            <div className="font-['Primary-Medium'] text-kit-xs lowercase text-[var(--inyc-primary-text-color-light)]">
              unsaved changes
            </div>
          )}
        </div>
      )}
      
      {/* Always-visible header: title + tabs (business edit mode only) */}
      {isBusiness && isEditing && (
        <div className={`px-kit-32 ${isModalVariant ? "pt-kit-20" : "pt-kit-32"}`}>
          {!isModalVariant && <PortalTitle>edit event</PortalTitle>}
          <div className="flex gap-kit-8 w-full mt-kit-16 mb-kit-8">
            {portalTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setPortalTab(tab)}
                className={`flex-1 py-kit-12 text-center font-['Primary-Bold'] text-kit-md rounded-[var(--inyc-input-border-radius)] border-[1px] transition-all duration-200 ${
                  portalTab === tab
                    ? "border-[var(--inyc-secondary)] bg-[var(--inyc-secondary)] text-[var(--inyc-primary)]"
                    : "border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:border-[var(--inyc-secondary)]/50"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Event tab content — hidden when on lineup tab, but stays mounted */}
      <div style={{ display: canManageLineup && portalTab === "lineup" ? "none" : "block" }}>
        {!isEditing && !isModalVariant && (
          <PortalTitle />
        )}
        <EditEvent
          allowExtraDates
          recommend
          Ref={eventRef}
          handleError={handleError}
          edit={eventData}
          onSubmit={onSubmit}
          userLocation={userLocation}
          noPortalTitle={!isEditing || (isBusiness && isEditing)}
          flushTop={isBusiness && isEditing}
          onDateChange={handleDateChange}
          hideArtistTagging={hideArtistTagging}
        />

        {showPerformerSlots && (
          <PerformerSlotsEditor
            ref={slotsRef}
            initialData={eventData}
            eventDate={liveEventDate || eventData?.date}
            lockedRosterId={lockedRosterId}
            lockedRosterName={lockedRosterName}
          />
        )}

        <div className={`posting-form-actions mx-auto mt-kit-40 flex max-w-[90%] flex-col items-stretch text-center${submitConfirming ? " is-confirming" : ""}`}>
          {postError && (
            <div className="mb-kit-12 text-center">
              <ErrorMessage hideIcon className="!text-kit-sm !font-['Primary-Bold'] !font-bold">
                {postError}
              </ErrorMessage>
            </div>
          )}
          <div className="posting-form-actions-row">
          {!submitConfirming && (
            <>
              <div className="posting-form-actions-btn">
                <KitButton variant="secondary" fullWidth disabled={loading || savingTemplate} onClick={previewEvent}>
                  preview
                </KitButton>
              </div>
              <div className="posting-form-actions-btn">
                <KitButton variant="secondary" fullWidth loading={savingTemplate} disabled={loading} onClick={saveAsTemplate}>
                  save template
                </KitButton>
              </div>
            </>
          )}
          <div className="posting-form-actions-btn">
            {submitConfirming ? (
              <div className="flex flex-col items-center gap-kit-8">
                <div className="font-['Primary-Medium'] text-kit-md">confirm submission?</div>
                <div className="flex items-center justify-center gap-kit-8">
                  <KitButton variant="secondary" className="min-w-[8.5rem]" onClick={() => setSubmitConfirming(false)}>
                    no
                  </KitButton>
                  <KitButton
                    variant="primary"
                    className="min-w-[8.5rem]"
                    onClick={() => {
                      setSubmitConfirming(false);
                      onSubmit();
                    }}
                  >
                    yes
                  </KitButton>
                </div>
              </div>
            ) : (
              <KitButton variant="primary" fullWidth loading={loading} disabled={savingTemplate} onClick={() => setSubmitConfirming(true)}>
                {submitLabel}
              </KitButton>
            )}
          </div>
          </div>
          {!submitConfirming && (
            <div className="posting-form-actions-note font-bold text-kit-base text-[var(--inyc-primary-text-color-light)]">
              templates save event info for easier future posting
            </div>
          )}
        </div>
      </div>

      {/* Lineup tab — edit mode + business only */}
      {portalTab === "lineup" && canManageLineup && (
        <LineupTab eventId={eventData._id} />
      )}
    </div>
  );
};

const NewEvent = ({ userLocation }) => (
  <EventPostingSurface userLocation={userLocation} />
);

export default NewEvent;
